import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
// **The registration lives here, in the one module that loads glTF.** `LoadAssetContainerAsync`
// resolves a plugin off a registry, and with nothing registered it fails with *No plugin or
// fallback for …* — a message about URLs that says nothing about glTF. Beside the only call site it
// cannot be forgotten by a new entry point, and the tests deliberately do **not** import it: a test
// that registers its own loader passes while production has none.
import '@babylonjs/loaders/glTF/2.0';

import { renderFeatures } from '../compat';

/**
 * Loading a generated `.glb` — the characters (`skinned.ts`) and the props (`props.ts`) alike.
 *
 * Both are Hunyuan3D-2 exports run through `scripts/prep-body.py`, and both need the same two
 * things done on the way in: the plugin named, and the material made a `StandardMaterial`.
 */

/**
 * Loads a `.glb` once, as a container to instantiate from, with its materials converted.
 *
 * Returns `null` rather than throwing: every caller degrades to procedural geometry, and a
 * cosmetic upgrade that can take the scene with it is worse than no upgrade.
 */
export async function loadGlbContainer(scene: Scene, url: string, lift = SELF_LIGHT): Promise<AssetContainer | null> {
  try {
    // **`pluginExtension` is not optional here.** The loader picks its plugin off the file
    // extension, and a `data:` URL has none — so a file handed over as base64 (which is how the
    // tests feed one in, there being no server) fails with *No plugin or fallback for data:…*
    // rather than with anything about glTF. Naming the plugin makes both paths take the same one.
    const container = await LoadAssetContainerAsync(url, scene, { pluginExtension: '.glb' });
    useStandardMaterials(container, scene, lift);
    return container;
  } catch (error) {
    console.error(`grind: ${url.startsWith('data:') ? 'an inline .glb' : url} failed to load\n`, error);
    return null;
  }
}

/**
 * How much a painted model lights itself, as a fraction of its own texture. **Chosen by looking**,
 * against the crypts — the darkest biome — where 0.05 of flat grey made the obstacles vanish.
 * A character moves and is lit by its own nameplate and outline; a prop has only its value.
 */
export const SELF_LIGHT = 0.35;
export const PROP_SELF_LIGHT = 0.6;

/**
 * Swaps each glTF `PBRMaterial` for a `StandardMaterial` wearing the same texture. Once per
 * container, so every clone shares the result.
 *
 * ── Why, since the loader already built a working material ──────────────────────────────
 *
 * **Because in the game it never became ready, and the body was invisible with its shadow on
 * the floor.** A PBR material waits on the scene's environment BRDF lookup texture, which Babylon
 * decodes with the `rgbdDecode` post-process — and that shader is *fetched by URL* when nothing
 * imported it. Behind Caddy the fetch came back as the SPA's `index.html`, the compile failed on
 * its `<` (`Uniforms: scale textureSampler`), the lookup texture stayed not-ready forever, and so
 * did every PBR material. Nothing else errors: the shadow pass needs no material and drew the body
 * perfectly. A preview served straight off Vite fetched the shader and looked fine, which is what
 * made it look like a game-only fault.
 *
 * `StandardMaterial` needs no lookup texture, and it is what every other surface in this game is:
 * the palette's lift and specular, fog, the `?specular=0` unlit path. A metal-rough material with
 * no environment to reflect was also why the painted bodies read darker than the floor.
 *
 * Numbers chosen, not specified: the lift and specular are the palette's `steel`-range values
 * (`palette.ts` `SPECS`), which is what the boxes wore.
 */
export function useStandardMaterials(container: AssetContainer, scene: Scene, lift = SELF_LIGHT): void {
  for (const mesh of container.meshes) {
    const pbr = mesh.material as PBRMaterial | null;
    if (!pbr || pbr.getClassName() !== 'PBRMaterial') continue;
    const tex = pbr.albedoTexture;
    const mat = new StandardMaterial(`${pbr.name}-std`, scene);
    mat.diffuseColor = new Color3(1, 1, 1);
    mat.specularColor = new Color3(0.06, 0.06, 0.07);
    mat.specularPower = 16;
    mat.emissiveColor = new Color3(0.05, 0.05, 0.055);
    mat.fogEnabled = renderFeatures.fog;
    if (tex) {
      mat.diffuseTexture = tex;
      // **A lift in the painting's own colours**, the emissive the palette gives every procedural
      // surface: the world is near-black and a model has to out-value it to read as a silhouette.
      // A flat 0.05 left a crypt's sarcophagus the colour of its floor — and an obstacle is solid,
      // so one nobody can see is one they walk into. A clone, because `level` is per texture and the
      // diffuse must stay at 1; the clone shares the GPU texture, so it costs nothing to hold.
      const glowTex = tex.clone();
      if (glowTex) {
        glowTex.level = lift;
        mat.emissiveTexture = glowTex;
        mat.emissiveColor = new Color3(0, 0, 0);
      }
      if (!renderFeatures.specular) {
        // The palette's unlit path: with lighting stripped the texture has to drive emission,
        // or the body goes black.
        mat.disableLighting = true;
        mat.emissiveTexture = tex;
        mat.emissiveColor = new Color3(0, 0, 0);
      }
    }
    mesh.material = mat;
    container.materials.push(mat);
    // The texture is the new material's now; only the PBR shell goes.
    pbr.dispose(false, false);
    const at = container.materials.indexOf(pbr);
    if (at !== -1) container.materials.splice(at, 1);
  }
}
